import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { SelectOption, UserOption } from '../../../components/'
import { apiService } from '../services/api'
import { useUsuarios, useSetores } from '../hooks/useData'
import { authService } from '../../../services/auth'
import { toast } from '../../../components/Toast'
import type { Processo } from '../../../models'
import type { TemplateSchema } from '../../templates'
import {
  pagamentoSchema,
  getSolicitacaoAcessoSistemaSchema,
  SOLICITACAO_ACESSO_SISTEMA_FLUXO_SIMPLIFICADO,
  solicitacaoCriacaoAcessoInstitucionalSchema,
} from '../../templates'

export type ProcessoLink = NonNullable<Processo['links']>[number] | string

export type ProcessoAnexo = NonNullable<Processo['anexos']>[number] | string

export type ProcessoAutorizador = NonNullable<Processo['autorizadores']>[number]

export type ProcessoOption = SelectOption & {
  setor_processo_fk: string | number
  template_schema?: TemplateSchema | null
  instrucoes?: string
  links?: ProcessoLink[]
  anexos?: ProcessoAnexo[]
  descricao?: string
  autorizadores?: ProcessoAutorizador[]
  gestor_demandante?: boolean | number | null
  executivo_demandante?: boolean | number | null
  executivo_demandado?: boolean | number | null
  gestor_demandado?: boolean | number | null
}

type ProcessoRecord = Omit<Processo, 'template_schema'> & {
  template_schema: TemplateSchema | null
}

// Event para notificar atualização de demandas
const DEMAND_CREATED_EVENT = 'demand-created'
const triggerDemandRefresh = (delayMs = 2000) => {
  // Aguarda processamento no servidor antes de disparar refresh
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent(DEMAND_CREATED_EVENT))
  }, delayMs)
}

// Configurações padrão de fluxo simplificado para templates conhecidos
const fluxoSimplificadoDefaults: Record<string, TemplateSchema['fluxoSimplificado'] | undefined> = {
  solicitacao_pagamento: pagamentoSchema.fluxoSimplificado,
  solicitacao_acesso_sistema: SOLICITACAO_ACESSO_SISTEMA_FLUXO_SIMPLIFICADO,
  solicitacao_criacao_acesso_institucional: solicitacaoCriacaoAcessoInstitucionalSchema.fluxoSimplificado,
}

const withBlockTitulo = (schema: TemplateSchema): Pick<TemplateSchema, 'blockTitulo'> | undefined => {
  if (schema.blockTitulo === undefined) {
    return undefined
  }

  return { blockTitulo: schema.blockTitulo }
}

// Defaults de campos top-level do TemplateSchema (não pertencem ao fluxoSimplificado)
const templateTopLevelDefaults: Record<string, Pick<TemplateSchema, 'blockTitulo'> | undefined> = {
  solicitacao_pagamento: withBlockTitulo(pagamentoSchema),
  solicitacao_criacao_acesso_institucional: withBlockTitulo(solicitacaoCriacaoAcessoInstitucionalSchema),
}

const mergeFluxoSimplificadoWithDefaults = (
  templateSchema: TemplateSchema | null
): TemplateSchema | null => {
  if (!templateSchema?.template_id) {
    return templateSchema
  }

  const topLevelDefaults = templateTopLevelDefaults[templateSchema.template_id]
  const defaultConfig = fluxoSimplificadoDefaults[templateSchema.template_id]

  if (!defaultConfig && !topLevelDefaults) {
    return templateSchema
  }

  const currentConfig = templateSchema.fluxoSimplificado
  const mergedTituloCondicional =
    currentConfig?.tituloCondicional ?? defaultConfig?.tituloCondicional

  const mergedConfig: TemplateSchema['fluxoSimplificado'] = defaultConfig
    ? {
        ...defaultConfig,
        ...(currentConfig || {}),
        ...(mergedTituloCondicional ? { tituloCondicional: mergedTituloCondicional } : {}),
      }
    : currentConfig

  return {
    ...templateSchema,
    ...(topLevelDefaults || {}),
    ...(mergedConfig !== undefined ? { fluxoSimplificado: mergedConfig } : {}),
  }
}

const normalizeProcessId = (value: string | number) => Number(value)

const normalizeBooleanLike = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1' || normalized === 'sim') return true
    if (normalized === 'false' || normalized === '0' || normalized === 'nao' || normalized === 'não') return false
  }
  return null
}

const matchesConditionalValue = (value: unknown, expected: string | number | boolean): boolean => {
  if (typeof expected === 'boolean') {
    const normalized = normalizeBooleanLike(value)
    return normalized === expected
  }

  return String(value ?? '').trim() === String(expected).trim()
}

const resolveTemplateDemandTitle = ({
  templateSchema,
  templateData,
  manualTitle,
  fallbackTitle,
}: {
  templateSchema: TemplateSchema | null | undefined
  templateData: Record<string, unknown> | null | undefined
  manualTitle: string | undefined
  fallbackTitle: string | undefined
}): string => {
  const normalizedManual = (manualTitle || '').trim()
  const normalizedFallback = (fallbackTitle || '').trim()

  const rule = templateSchema?.fluxoSimplificado?.tituloCondicional
  if (rule && templateData) {
    const baseTitle = templateData[rule.campoTitulo]
    if (typeof baseTitle === 'string' && baseTitle.trim().length > 0) {
      let title = baseTitle.trim()
      // Adiciona sufixo se a condição for verdadeira
      if (rule.sufixoSeCondicaoVerdadeira) {
        const conditionValue = templateData[rule.campoCondicao]
        if (matchesConditionalValue(conditionValue, rule.valorCondicao)) {
          title += rule.sufixoSeCondicaoVerdadeira
        }
      }
      return title
    }
  }

  return normalizedManual || normalizedFallback || 'Solicitação'
}

export const useNewDemandLogic = () => {
  // Hook de navegação e URL params
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Obter relacionada_fk da URL (se existir)
  const relacionadaFkFromUrl = searchParams.get('relacionada_fk')
  const relacionadaFk = relacionadaFkFromUrl ? Number(relacionadaFkFromUrl) : null

  // Estados básicos do formulário
  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [dataEntrega, setDataEntrega] = useState('')
  const [demandante, setDemandante] = useState<string | number>('')
  const [prioridade, setPrioridade] = useState<string>('Media') // Padrão sem acento
  const [euCriei, setEuCriei] = useState(false)
  const [observadores, setObservadores] = useState<(string | number)[]>([])
  const [anexos, setAnexos] = useState<File[]>([])

  // Estados para SLA (dias úteis calculados)
  const [slaData, setSlaData] = useState<
    Map<
      string | number,
      {
        baixa: string
        media: string
        alta: string
        urgente: string
      }
    >
  >(new Map())
  const [loadingSLA, setLoadingSLA] = useState(false)

  // Estado para controlar se o usuário editou manualmente a data de entrega
  const [dataEntregaEditadaManualmente, setDataEntregaEditadaManualmente] = useState(false)

  // Estados específicos para o formulário progressivo
  const [selectedProcesses, setSelectedProcesses] = useState<(string | number)[]>([])
  const [selectedSectors, setSelectedSectors] = useState<(string | number)[]>([])

  // Estados de controle
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Estados para upload progressivo
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'validating' | 'creating' | 'uploading' | 'success' | 'error'
  >('idle')
  const [uploadMessage, setUploadMessage] = useState('')
  const [progressInterval, setProgressInterval] = useState<ReturnType<typeof setInterval> | null>(
    null
  )

  // Estados para customização por processo
  const [customizationMode, setCustomizationMode] = useState<'simple' | 'custom'>('simple')
  const [processCustomData, setProcessCustomData] = useState<
    Map<
      string | number,
      {
        descricao: string
        anexos: File[]
      }
    >
  >(new Map())

  // Estado para respostas de templates dinâmicos
  // Mapa de processo_id -> respostas do formulário do template
  const [templateRespostas, setTemplateRespostas] = useState<
    Map<string | number, Record<string, unknown>>
  >(new Map())

  // Buscar dados do backend
  const { usuarios, loading: loadingUsuarios, error: errorUsuarios } = useUsuarios()
  const { setores, loading: loadingSetores, error: errorSetores } = useSetores()

  // Estados para processos (agora controlados localmente para filtro)
  const [processos, setProcessos] = useState<ProcessoRecord[]>([])
  const [loadingProcessos, setLoadingProcessos] = useState(false)

  // Schema dinâmico fresco do RQ35 (carregado do backend, nunca do BD do processo)
  const [freshSolicitacaoAcessoSchema, setFreshSolicitacaoAcessoSchema] = useState<TemplateSchema | null>(null)

  // Obter usuário atual
  const usuarioAtual = authService.getUser()

  // Cleanup effect para limpar intervals
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }, [progressInterval])

  // Carregar schema de solicitação de acesso ao sistema (estático)
  useEffect(() => {
    setFreshSolicitacaoAcessoSchema(getSolicitacaoAcessoSistemaSchema())
  }, [])

  // Carregar todos os processos inicialmente
  useEffect(() => {
    const loadProcessos = async () => {
      try {
        setLoadingProcessos(true)
        const data = await apiService.getProcessos()
        const normalized: ProcessoRecord[] = data.map(proc => {
          const templateSchema = (proc.template_schema as TemplateSchema | null | undefined) ?? null

          const templateWithDefaults = mergeFluxoSimplificadoWithDefaults(templateSchema)

          return {
            ...proc,
            template_schema: templateWithDefaults ?? null,
          } satisfies ProcessoRecord
        })

        setProcessos(normalized)
      } catch (error) {
        console.error('Erro ao carregar processos:', error)
        toast.error('Erro ao carregar processos')
      } finally {
        setLoadingProcessos(false)
      }
    }

    loadProcessos()
  }, [])

  // ID especial para o "setor" virtual de Templates (precisa bater com o usado no UnifiedProcessSelector)
  const TEMPLATES_SECTOR_ID = '__TEMPLATES__'

  // Limpar processos selecionados quando setores mudarem.
  // Importante: NÃO filtrar a lista enviada ao UnifiedProcessSelector, senão o chip "Templates Rápidos"
  // desaparece quando o subset atual não contém nenhum template.
  useEffect(() => {
    if (selectedSectors.length === 0) return

    const templatesSelected = selectedSectors.includes(TEMPLATES_SECTOR_ID)
    const regularSectorsSelected = selectedSectors.filter(s => s !== TEMPLATES_SECTOR_ID)

    const validProcessIds = processos
      .filter(proc => {
        const isTemplateMatch = templatesSelected && !!proc.template_schema
        const isSectorMatch =
          regularSectorsSelected.length > 0 && regularSectorsSelected.includes(proc.setor_processo_fk)
        return isTemplateMatch || isSectorMatch
      })
      .map(proc => proc.id)

    setSelectedProcesses(prev => prev.filter(id => validProcessIds.includes(normalizeProcessId(id))))
  }, [selectedSectors, processos])

  // Detectar se processos selecionados têm instruções e configurar modo de customização
  useEffect(() => {
    if (selectedProcesses.length > 0) {
      // SEMPRE usar modo 'custom' se houver múltiplos processos
      // OU se algum processo tiver conteúdo adicional (instruções, links ou anexos)
      const isMultipleProcesses = selectedProcesses.length > 1

      const hasAdditionalContent = selectedProcesses.some(processId => {
        const processo = processos.find(p => p.id === normalizeProcessId(processId))

        // Verificar se tem instruções
        const hasInstructions = processo?.instrucoes && processo.instrucoes.trim() !== ''

        // Verificar se tem links
        const hasLinks = Array.isArray(processo?.links) && processo.links.length > 0

        // Verificar se tem anexos
        const hasAnexos = Array.isArray(processo?.anexos) && processo.anexos.length > 0

        return hasInstructions || hasLinks || hasAnexos
      })

      // Usar modo 'custom' se for múltiplos processos OU se houver conteúdo adicional
      const shouldUseCustomMode = isMultipleProcesses || hasAdditionalContent
      setCustomizationMode(shouldUseCustomMode ? 'custom' : 'simple')

      // Se deve usar modo custom, atualizar dados customizados preservando edições existentes
      if (shouldUseCustomMode) {
        setProcessCustomData(prevMap => {
          const newMap = new Map(prevMap) // Preservar dados existentes

          selectedProcesses.forEach(processId => {
            // Só inicializar se ainda não existe no Map
            if (!newMap.has(processId)) {
              const processo = processos.find(p => p.id === normalizeProcessId(processId))
              if (processo) {
                // Pré-popular descrição com instruções se disponível
                // Usuário pode editar/complementar conforme necessário
                newMap.set(processId, {
                  descricao: processo.instrucoes || '', // Pré-preenche com instruções
                  //descricao: '', // campo de descrição da demanda vazio para o usuário preencher
                  anexos: [],
                })
              }
            }
          })

          // Remover processos que foram desmarcados
          const currentProcessIds = new Set(selectedProcesses)
          for (const key of newMap.keys()) {
            if (!currentProcessIds.has(key)) {
              newMap.delete(key)
            }
          }

          return newMap
        })
      } else {
        // Limpar dados customizados se não deve usar modo custom
        setProcessCustomData(new Map())
      }
    } else {
      setCustomizationMode('simple')
      setProcessCustomData(new Map())
    }
  }, [selectedProcesses, processos])

  // Buscar SLA (dias úteis) quando processos ou prioridade mudarem
  useEffect(() => {
    const fetchSLAs = async () => {
      if (selectedProcesses.length === 0 || !prioridade) {
        setSlaData(new Map())
        return
      }

      setLoadingSLA(true)
      const newSlaData = new Map()

      try {
        // Buscar SLA para cada processo selecionado
        await Promise.all(
          selectedProcesses.map(async processId => {
            try {
              const sla = await apiService.getProcessoDiasUteis(Number(processId))
              newSlaData.set(processId, sla)
            } catch (error) {
              console.error(`Erro ao buscar SLA do processo ${processId}:`, error)
            }
          })
        )

        setSlaData(newSlaData)

        // Preencher automaticamente a data de entrega com o SLA do primeiro processo
        // MAS APENAS se o usuário não tiver editado manualmente a data
        if (selectedProcesses.length > 0 && !dataEntregaEditadaManualmente) {
          const firstProcessId = selectedProcesses[0]
          const firstProcessSla = newSlaData.get(firstProcessId)

          if (firstProcessSla) {
            const prioridadeKey = prioridade.toLowerCase() as 'baixa' | 'media' | 'alta' | 'urgente'
            const slaDate = firstProcessSla[prioridadeKey]

            if (slaDate) {
              // Converter ISO para formato YYYY-MM-DD
              const date = new Date(slaDate)
              const formattedDate = date.toISOString().split('T')[0]

              setDataEntrega(formattedDate)
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar SLAs:', error)
      } finally {
        setLoadingSLA(false)
      }
    }

    fetchSLAs()
  }, [selectedProcesses, prioridade, dataEntregaEditadaManualmente])

  // Atualiza título reativamente quando o usuário preenche campos do template
  // (ex: tituloCondicional no template de pagamento)
  useEffect(() => {
    if (selectedProcesses.length !== 1) return

    const processoId = selectedProcesses[0]
    const processo = processos.find(p => p.id === normalizeProcessId(processoId))
    const templateSchema = mergeFluxoSimplificadoWithDefaults(processo?.template_schema ?? null)

    if (!templateSchema?.fluxoSimplificado?.tituloCondicional) return

    const respostas = templateRespostas.get(processoId) ?? null
    const rule = templateSchema.fluxoSimplificado!.tituloCondicional!

    // Só atualiza o título se o campo base (ex: tipo_pagamento) tiver valor
    const baseTitle = respostas?.[rule.campoTitulo]
    if (!baseTitle || typeof baseTitle !== 'string' || !baseTitle.trim()) return

    const resolved = resolveTemplateDemandTitle({
      templateSchema,
      templateData: respostas,
      manualTitle: undefined,
      fallbackTitle: undefined,
    })

    setTitulo(resolved)
  }, [templateRespostas, selectedProcesses, processos])

  // Converter processos para opções de select (incluindo campos extras e autorizadores)
  const processoOptions: ProcessoOption[] = processos.map(processoCompleto => {
    // Para o template RQ35 (dinâmico), usar sempre o schema fresco do backend em vez
    // do armazenado no banco de dados, garantindo que módulos/perfis adicionados
    // após a criação do processo sejam exibidos corretamente.
    const isDynamicRQ35 =
      processoCompleto.template_schema?.template_id === 'solicitacao_acesso_sistema'
    const baseSchema =
      isDynamicRQ35 && freshSolicitacaoAcessoSchema
        ? freshSolicitacaoAcessoSchema
        : (processoCompleto.template_schema ?? null)
    const templateSchema: TemplateSchema | null = mergeFluxoSimplificadoWithDefaults(baseSchema)

    const links = Array.isArray(processoCompleto.links)
      ? (processoCompleto.links as ProcessoLink[])
      : undefined

    const anexos = Array.isArray(processoCompleto.anexos)
      ? (processoCompleto.anexos as ProcessoAnexo[])
      : undefined

    const option: ProcessoOption = {
      value: processoCompleto.id,
      label: processoCompleto.nome_processo,
      setor_processo_fk: processoCompleto.setor_processo_fk,
      template_schema: templateSchema,
    }

    if (processoCompleto.instrucoes) {
      option.instrucoes = processoCompleto.instrucoes
    }

    if (links) {
      option.links = links
    }

    if (anexos) {
      option.anexos = anexos
    }

    if (processoCompleto.descricao) {
      option.descricao = processoCompleto.descricao
    }

    if (processoCompleto.autorizadores) {
      option.autorizadores = processoCompleto.autorizadores
    }

    option.gestor_demandante = processoCompleto.gestor_demandante ?? null
    option.executivo_demandante = processoCompleto.executivo_demandante ?? null
    option.executivo_demandado = processoCompleto.executivo_demandado ?? null
    option.gestor_demandado = processoCompleto.gestor_demandado ?? null

    return option
  })

  // Converter usuários para opções de select
  const userOptions: UserOption[] = usuarios.map(user => ({
    value: user.id,
    label: user.name,
    name: user.name,
    email: user.email,
    ...(user.avatar && { avatar: user.avatar }),
  }))

  // Converter setores para opções de select
  const setorOptions: SelectOption[] = setores.map(setor => ({
    value: setor.id,
    label: setor.nome_setor,
  }))

  // Opções de prioridade (sem acentos para compatibilidade com backend)
  const prioridadeOptions: SelectOption[] = [
    { value: 'Baixa', label: 'Baixa' },
    { value: 'Media', label: 'Média' }, // Backend aceita 'Media' sem acento
    { value: 'Alta', label: 'Alta' },
    { value: 'Urgente', label: 'Urgente' },
  ]

  // Função para verificar se o fluxo simplificado está ativo
  // Retorna as configurações do fluxo simplificado se ativo, ou null caso contrário
  const getFluxoSimplificado = () => {
    // Fluxo simplificado só se aplica quando há exatamente 1 processo selecionado
    if (selectedProcesses.length !== 1) return null

    const processoId = selectedProcesses[0]
    const processo = processos.find(p => p.id === normalizeProcessId(processoId))

    // Verifica se o processo tem template com fluxo simplificado ativo
    const templateSchema = processo?.template_schema
    if (!templateSchema?.fluxoSimplificado?.ativo) return null

    return {
      processoId,
      processoNome: processo?.nome_processo || '',
      config: templateSchema.fluxoSimplificado,
    }
  }

  // Função para aplicar configurações do fluxo simplificado
  const aplicarFluxoSimplificado = (config: ReturnType<typeof getFluxoSimplificado>) => {
    if (!config) return

    // Auto-preencher título se configurado
    if (config.config.tituloAutomatico && config.processoNome) {
      setTitulo(config.processoNome)
    }

    // Auto-preencher demandante se configurado
    if (config.config.demandanteAutomatico && usuarioAtual) {
      setEuCriei(true)
      setDemandante(usuarioAtual.id)
    }

    // Auto-preencher prioridade se configurado
    if (config.config.prioridadePadrao) {
      setPrioridade(config.config.prioridadePadrao)
    }

    // Auto-preencher data de entrega se dias padrão configurado
    if (config.config.diasEntregaPadrao && config.config.diasEntregaPadrao > 0) {
      const dataEntregaCalculada = calcularDataEntrega(config.config.diasEntregaPadrao)
      setDataEntrega(dataEntregaCalculada)
      setDataEntregaEditadaManualmente(false) // Permite que o SLA ainda ajuste se necessário
    }
  }

  // Função auxiliar para calcular data de entrega baseada em dias úteis
  const calcularDataEntrega = (diasUteis: number): string => {
    const hoje = new Date()
    let diasContados = 0
    const data = new Date(hoje)

    while (diasContados < diasUteis) {
      data.setDate(data.getDate() + 1)
      const diaSemana = data.getDay()
      // Pular sábados (6) e domingos (0)
      if (diaSemana !== 0 && diaSemana !== 6) {
        diasContados++
      }
    }

    return data.toISOString().split('T')[0]
  }

  // Handlers com validação
  const handleTituloChange = (value: string) => {
    setTitulo(value)
    if (fieldErrors.titulo) {
      setFieldErrors(prev => ({ ...prev, titulo: '' }))
    }
  }

  const handleDescricaoChange = (value: string) => {
    setDescricao(value)
    if (fieldErrors.descricao) {
      setFieldErrors(prev => ({ ...prev, descricao: '' }))
    }
  }

  const handleDataEntregaChange = (value: string) => {
    setDataEntrega(value)

    // Marcar que o usuário editou manualmente a data
    // Isso impede que o SLA sobrescreva a data escolhida
    setDataEntregaEditadaManualmente(true)

    // Limpar erro ao digitar, mas só validar quando a data estiver completa
    if (value.length === 10) {
      const today = new Date().toISOString().split('T')[0]
      if (value < today) {
        setFieldErrors(prev => ({ ...prev, dataEntrega: 'Data não pode ser anterior a hoje' }))
      } else if (fieldErrors.dataEntrega) {
        setFieldErrors(prev => ({ ...prev, dataEntrega: '' }))
      }
    } else {
      // Limpar erro enquanto está digitando
      if (fieldErrors.dataEntrega) {
        setFieldErrors(prev => ({ ...prev, dataEntrega: '' }))
      }
    }
  }

  const handleDemandanteChange = (value: string | number) => {
    setDemandante(value)
    if (fieldErrors.demandante) {
      setFieldErrors(prev => ({ ...prev, demandante: '' }))
    }
  }

  const handlePrioridadeChange = (value: string | number) => {
    setPrioridade(String(value))

    // Quando a prioridade muda, resetar o flag de edição manual
    // para que a data seja automaticamente atualizada com o novo SLA
    setDataEntregaEditadaManualmente(false)

    if (fieldErrors.prioridade) {
      setFieldErrors(prev => ({ ...prev, prioridade: '' }))
    }
  }

  const handleObservadoresChange = (value: (string | number)[]) => {
    setObservadores(value)
  }

  const handleFileChange = (eventOrFiles: ChangeEvent<HTMLInputElement> | File[]) => {
    if (Array.isArray(eventOrFiles)) {
      setAnexos(eventOrFiles)
      return
    }

    const files = eventOrFiles.target.files
    if (files) {
      setAnexos(Array.from(files))
    }
  }

  // Handler para mudança do checkbox "Eu criei"
  const handleEuCrieiChange = (checked: boolean) => {
    setEuCriei(checked)
    if (checked && usuarioAtual) {
      setDemandante(usuarioAtual.id)
    } else {
      setDemandante('')
    }
  }

  // Função auxiliar para resetar o formulário
  const resetForm = () => {
    setTitulo('')
    setDescricao('')
    setDataEntrega('')
    setDataEntregaEditadaManualmente(false) // Resetar flag de edição manual
    setDemandante('')
    setPrioridade('Media')
    setEuCriei(false)
    setObservadores([])
    setSelectedProcesses([])
    setSelectedSectors([])
    setAnexos([])
  }

  // Função para enviar o formulário de forma otimizada
  const handleEnviar = async () => {
    try {
      setIsSubmitting(true)
      setSubmitError(null)
      setFieldErrors({})
      setUploadStatus('validating')
      setUploadMessage('Validando dados...')
      setUploadProgress(10)

      // Validações básicas
      const errors: Record<string, string> = {}

      const hasSingleSelectedProcess = selectedProcesses.length === 1
      const selectedProcessId = hasSingleSelectedProcess ? selectedProcesses[0] : null
      const selectedProcessForTitle = hasSingleSelectedProcess
        ? processos.find(p => p.id === normalizeProcessId(selectedProcessId as string | number))
        : null

      const selectedTemplateDataForTitle = selectedProcessId !== null
        ? (templateRespostas.get(selectedProcessId) ||
            templateRespostas.get(Number(selectedProcessId)) ||
            templateRespostas.get(String(selectedProcessId)) ||
            null)
        : null

      const autoResolvedTitle = resolveTemplateDemandTitle({
        templateSchema: selectedProcessForTitle?.template_schema,
        templateData: selectedTemplateDataForTitle,
        manualTitle: '',
        fallbackTitle: '',
      })
      const hasAutoResolvedTitle = autoResolvedTitle.trim().length > 0

      if (!titulo.trim() && !hasAutoResolvedTitle) {
        errors.titulo = 'Título é obrigatório'
      }

      if (!prioridade) {
        errors.prioridade = 'Prioridade é obrigatória'
      }

      // Validar descrição baseado no modo
      if (customizationMode === 'custom') {
        // Modo customizado: validar descrição de cada processo
        let hasEmptyDescription = false
        selectedProcesses.forEach(processoId => {
          const customData = processCustomData.get(processoId)
          if (!customData?.descricao || customData.descricao.trim() === '') {
            hasEmptyDescription = true
          }
        })
        if (hasEmptyDescription) {
          errors.descricao = 'Preencha a descrição de todos os processos'
        }
      } else {
        // Modo simples: verificar se tem template OU descrição
        // Buscar o processo selecionado para verificar se tem template
        const processoSelecionado = processos.find(
          p => p.id === normalizeProcessId(selectedProcesses[0])
        )
        const temTemplate = processoSelecionado?.template_schema

        if (temTemplate && selectedProcesses.length === 1) {
          // Com template: verificar se templateRespostas tem dados
          // Tentar buscar com ambos os tipos de chave (string e number)
          const processId = selectedProcesses[0]
          const templateData =
            templateRespostas.get(processId) ||
            templateRespostas.get(Number(processId)) ||
            templateRespostas.get(String(processId))
          const schema = processoSelecionado?.template_schema?.schema
          const requiredFields = schema?.required || []

          // Verificar campos obrigatórios do template
          if (requiredFields.length > 0) {
            const properties = schema?.properties || {}

            const missingFields = requiredFields.filter((field: string) => {
              const value = templateData?.[field]
              const fieldDef = properties[field]
              const isArrayField =
                fieldDef &&
                typeof fieldDef === 'object' &&
                !Array.isArray(fieldDef) &&
                'type' in fieldDef &&
                fieldDef.type === 'array'

              if (isArrayField) {
                // Validar array: deve ter pelo menos 1 item com campos required preenchidos
                if (!Array.isArray(value) || value.length === 0) return true
                const minItems =
                  'minItems' in fieldDef && typeof fieldDef.minItems === 'number'
                    ? fieldDef.minItems
                    : 1
                if (value.length < minItems) return true
                // Verificar campos required de cada item
                const itemDef = 'items' in fieldDef && fieldDef.items
                const itemRequired: string[] =
                  itemDef &&
                  typeof itemDef === 'object' &&
                  !Array.isArray(itemDef) &&
                  'required' in itemDef &&
                  Array.isArray(itemDef.required)
                    ? (itemDef.required as string[])
                    : []
                return value.some((item: Record<string, unknown>) =>
                  itemRequired.some(
                    req =>
                      item[req] === undefined ||
                      item[req] === null ||
                      (typeof item[req] === 'string' && (item[req] as string).trim() === '')
                  )
                )
              }

              return value === undefined || value === null || value === ''
            })

            if (missingFields.length > 0) {
              errors.descricao = 'Preencha todos os campos obrigatórios do formulário'
            }
          } else if (!templateData || Object.keys(templateData).length === 0) {
            // Se não tem campos obrigatórios definidos, exigir pelo menos algum preenchimento
            errors.descricao = 'Preencha o formulário'
          }
        } else {
          // Sem template: validar descrição global
          if (!descricao.trim()) {
            errors.descricao = 'Descrição é obrigatória'
          }
        }
      }

      if (!dataEntrega) {
        errors.dataEntrega = 'Data de entrega é obrigatória'
      }

      if (selectedProcesses.length === 0) {
        errors.processos = 'Selecione pelo menos um processo'
      }

      if (!euCriei && !demandante) {
        errors.demandante = 'Selecione um demandante ou marque "Eu"'
      }

      if (Object.keys(errors).length > 0) {
        console.error('Erros de validação:', errors)
        setFieldErrors(errors)
        setUploadStatus('error')
        setUploadMessage('Corrija os erros no formulário')
        toast.error('Por favor, corrija os erros no formulário', errors)
        return
      }

      // Preparar dados baseado no modo de customização
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let demandaData: any
      let hasAttachments = false

      if (customizationMode === 'custom') {
        // MODO CUSTOMIZADO: Cada processo tem sua própria demanda com dados específicos
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const demandasCustomizadas: any[] = []

        selectedProcesses.forEach(processoId => {
          const customData = processCustomData.get(processoId)
          // Normalizar chave para buscar dados do template (pode ser string ou number)
          const templateData =
            templateRespostas.get(processoId) ||
            templateRespostas.get(Number(processoId)) ||
            templateRespostas.get(String(processoId))

          const processoSelecionado = processos.find(
            p => p.id === normalizeProcessId(processoId)
          )

          const tituloDemanda = resolveTemplateDemandTitle({
            templateSchema: processoSelecionado?.template_schema,
            templateData,
            manualTitle: titulo,
            fallbackTitle:
              processoSelecionado?.nome_processo ||
              processoSelecionado?.template_schema?.schema?.title ||
              'Solicitação',
          })

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const demandaItem: any = {
            processo_fk: Number(processoId),
            demandante_fk: euCriei ? Number(usuarioAtual?.id) : Number(demandante),
            titulo: tituloDemanda,
            prioridade: prioridade,
            descricao: customData?.descricao || descricao.trim(),
            sugestao_entrega: dataEntrega,
            observadores: observadores.map(id => Number(id)),
            anexos: customData?.anexos || [],
            ...(relacionadaFk && { relacionada_fk: relacionadaFk }),
          }

          // Adicionar template_respostas se existir
          if (templateData && Object.keys(templateData).length > 0) {
            demandaItem.template_respostas = templateData
          }

          demandasCustomizadas.push(demandaItem)

          // Verificar se há anexos em qualquer processo
          if (customData?.anexos && customData.anexos.length > 0) {
            hasAttachments = true
          }
        })

        demandaData = { demandas: demandasCustomizadas }
      } else {
        // MODO SIMPLES: Dados compartilhados replicados para todos os processos

        // Verificar se está usando template
        const processoSelecionado = processos.find(
          p => p.id === normalizeProcessId(selectedProcesses[0])
        )
        const temTemplate = processoSelecionado?.template_schema && selectedProcesses.length === 1
        // Normalizar chave para buscar dados do template (pode ser string ou number)
        const processIdSimples = selectedProcesses[0]
        const templateData = temTemplate
          ? templateRespostas.get(processIdSimples) ||
            templateRespostas.get(Number(processIdSimples)) ||
            templateRespostas.get(String(processIdSimples))
          : null

        // Se tem template, gerar descrição a partir das respostas ou usar placeholder
        let descricaoFinal = descricao.trim()
        if (temTemplate && templateData && Object.keys(templateData).length > 0) {
          // Gerar descrição resumida das respostas do template
          const schema = processoSelecionado?.template_schema?.schema
          const properties = schema?.properties || {}

          const descricaoParts: string[] = []
          Object.entries(templateData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              const definition = properties?.[key]
              const fieldTitle =
                definition &&
                typeof definition === 'object' &&
                !Array.isArray(definition) &&
                'title' in definition &&
                typeof definition.title === 'string'
                  ? definition.title
                  : key
              descricaoParts.push(`${fieldTitle}: ${value}`)
            }
          })

          descricaoFinal =
            descricaoParts.length > 0 ? descricaoParts.join('\n') : 'Preenchido via template'
        }

        const tituloFinal = resolveTemplateDemandTitle({
          templateSchema: processoSelecionado?.template_schema,
          templateData,
          manualTitle: titulo,
          fallbackTitle:
            processoSelecionado?.nome_processo ||
            processoSelecionado?.template_schema?.schema?.title ||
            'Solicitação',
        })

        demandaData = {
          processo_fk: selectedProcesses.map(id => Number(id)),
          demandante_fk: euCriei ? Number(usuarioAtual?.id) : Number(demandante),
          titulo: tituloFinal,
          prioridade: prioridade,
          descricao: descricaoFinal,
          sugestao_entrega: dataEntrega,
          observadores: observadores.map(id => Number(id)),
          ...(relacionadaFk && { relacionada_fk: relacionadaFk }),
        }

        // No modo simples, se houver apenas um processo e ele tiver template, incluir respostas
        if (temTemplate && templateData && Object.keys(templateData).length > 0) {
          // Formatar no formato esperado pelo backend
          demandaData.template_respostas = {
            template_id: processoSelecionado?.template_schema?.template_id || 'unknown',
            template_versao: processoSelecionado?.template_schema?.versao || '1.0',
            respostas: templateData,
          }
        }

        hasAttachments = anexos.length > 0
      }

      // Estratégia otimizada baseada na presença de anexos
      if (!hasAttachments) {
        // SEM ANEXOS: UI Otimista - navegação instantânea, criação em background
        setUploadStatus('success')
        setUploadMessage('Demanda enviada!')
        setUploadProgress(100)

        // Feedback otimista imediato
        toast.success('Demanda criada com sucesso!')

        // Resetar formulário
        resetForm()

        // Navegação INSTANTÂNEA (UX otimista)
        navigate('/demands/sent')

        // Criar demanda em background (não bloqueia UI)
        if (customizationMode === 'custom') {
          // Modo customizado: enviar com FormData
          apiService
            .createDemandaCustomizada(demandaData.demandas)
            .then(() => {
              toast.success('Demandas confirmadas no servidor!', {
                duration: 2000,
              })
              // Disparar evento para atualizar lista após processamento no servidor
              triggerDemandRefresh(2500) // 2.5s de delay para garantir processamento
            })
            .catch(error => {
              console.error('Erro ao confirmar demandas:', error)
              toast.error('Erro ao confirmar demandas. Verifique na lista.', {
                duration: 4000,
              })
            })
        } else {
          // Modo simples: enviar normalmente
          apiService
            .createDemanda(demandaData)
            .then(() => {
              toast.success('Demanda confirmada no servidor!', {
                duration: 2000,
              })
              // Disparar evento para atualizar lista após processamento no servidor
              triggerDemandRefresh(2500) // 2.5s de delay para garantir processamento
            })
            .catch(error => {
              console.error('Erro ao confirmar demanda:', error)
              toast.error('Erro ao confirmar demanda. Verifique na lista.', {
                duration: 4000,
              })
            })
        }

        return // Sair da função - não continuar com o resto
      } else {
        // COM ANEXOS: Upload com progress indicator
        setUploadStatus('creating')
        setUploadMessage('Criando demanda...')
        setUploadProgress(30)

        // Contar total de anexos
        const totalAnexos =
          customizationMode === 'custom'
            ? Array.from(processCustomData.values()).reduce(
                (sum, data) => sum + data.anexos.length,
                0
              )
            : anexos.length

        // Progresso mais lento e realista
        setUploadMessage(
          `Enviando demanda com ${totalAnexos} anexo${totalAnexos > 1 ? 's' : ''}...`
        )
        setUploadStatus('uploading')

        const interval = setInterval(() => {
          setUploadProgress(prev => {
            // Progresso mais lento até 70%, depois bem devagar até 90%
            if (prev < 50) return prev + 3
            if (prev < 70) return prev + 2
            if (prev < 90) return prev + 1
            return prev // Para em 90% e espera o API terminar
          })
        }, 300) // Intervalo mais lento (300ms vs 200ms)

        setProgressInterval(interval)

        // Executar API call baseado no modo
        if (customizationMode === 'custom') {
          await apiService.createDemandaCustomizada(demandaData.demandas)
        } else {
          await apiService.createDemanda(demandaData, anexos)
        }

        // API terminou - completar rapidamente
        if (interval) clearInterval(interval)
        setProgressInterval(null)

        // Animação rápida e satisfatória de 90% para 100%
        setUploadMessage('Finalizando envio...')
        setUploadProgress(95)

        setTimeout(() => {
          setUploadProgress(100)
          setUploadStatus('success')
          setUploadMessage('Demanda e anexos enviados com sucesso!')
        }, 200) // Animação rápida mas visível

        // Feedback de sucesso
        toast.success('Demanda criada com sucesso!')

        // Resetar formulário
        resetForm()

        // Navegação após mostrar sucesso brevemente
        setTimeout(() => {
          navigate('/demands/sent')
        }, 1200) // Tempo reduzido para navegar mais rápido
      }
    } catch (error) {
      console.error('Erro ao criar demanda:', error)

      // Limpar interval se estiver rodando
      if (progressInterval) {
        clearInterval(progressInterval)
        setProgressInterval(null)
      }

      setUploadStatus('error')
      setUploadProgress(0)

      if (error instanceof Error) {
        setSubmitError(error.message)
        setUploadMessage(`Erro: ${error.message}`)
      } else {
        setSubmitError('Erro inesperado ao criar demanda. Tente novamente.')
        setUploadMessage('Erro inesperado. Tente novamente.')
      }

      toast.error('Erro ao criar demanda. Verifique os dados e tente novamente.')
    } finally {
      setIsSubmitting(false)
      // Reset do status após um tempo se não for sucesso
      if (uploadStatus !== 'success') {
        setTimeout(() => {
          setUploadStatus('idle')
          setUploadMessage('')
          setUploadProgress(0)
          // Limpar interval se ainda estiver ativo
          if (progressInterval) {
            clearInterval(progressInterval)
            setProgressInterval(null)
          }
        }, 3000)
      }
    }
  }

  return {
    // Estados do formulário
    titulo,
    descricao,
    dataEntrega,
    demandante,
    prioridade,
    euCriei,
    setEuCriei: handleEuCrieiChange,
    observadores,
    anexos,
    relacionadaFk, // ID da demanda relacionada (se vier da URL)

    // Estados específicos do formulário progressivo
    selectedProcesses,
    setSelectedProcesses,
    selectedSectors,
    setSelectedSectors,

    // Estados de customização
    customizationMode,
    processCustomData,
    setProcessCustomData,

    // Estado para respostas de templates dinâmicos
    templateRespostas,
    setTemplateRespostas,

    // Estados de SLA
    slaData,
    loadingSLA,

    // Opções para os selects
    processoOptions,
    userOptions,
    setorOptions,
    prioridadeOptions,

    // Estados de loading e erro
    loading: loadingUsuarios || loadingSetores || loadingProcessos,
    isSubmitting,
    submitError,
    fieldErrors,
    errorUsuarios,
    errorSetores,

    // Estados de upload progressivo
    uploadProgress,
    uploadStatus,
    uploadMessage,

    // Handlers específicos com validação
    handleTituloChange,
    handleDescricaoChange,
    handleDataEntregaChange,
    handleDemandanteChange,
    handlePrioridadeChange,
    handleObservadoresChange,

    // Handlers
    handleFileChange,
    handleEnviar,

    // Usuário atual
    usuarioAtual,

    // Funções de fluxo simplificado
    getFluxoSimplificado,
    aplicarFluxoSimplificado,
  }
}
